import { useMutation } from '@tanstack/react-query'
import { bulkUpdateBFFOperations } from '../../BFF/bulkUpdates'
import { OpalApiRequestBody, OpalApiResponseDocument } from '../../schema'

type BulkUpdateResponseDocument =
  | OpalApiResponseDocument<'MomentsAddToPlanBFFV1', 200>
  | OpalApiResponseDocument<'MomentsAddToPlanBFFV1', 202>
type BulkUpdateRequestBody = OpalApiRequestBody<'MomentsAddToPlanBFFV1'>

type MomentData = BulkUpdateRequestBody['data']['attributes']['moments']

type CreatBulkUpdateParams<TSelectionMeta extends Record<string, unknown>> = {
  categoryId?: string
  categoryTypeId?: string
  moments: MomentData
  planId: string
  workspaceId: string
  /**
   * Optional argument that can be used to pass full resources and other data
   * to onMutate and onSuccess callbacks for use in things like toasts
   */
  selectionMeta?: TSelectionMeta
}

type QueryParams<TSelectionMeta extends Record<string, unknown>> = {
  onMutate?: (params: CreatBulkUpdateParams<TSelectionMeta>) => void
  onSuccess?: (
    data: BulkUpdateResponseDocument | undefined,
    params: CreatBulkUpdateParams<TSelectionMeta>
  ) => void
}

const buildRequestBody = <TSelectionMeta extends Record<string, unknown>>({
  categoryId,
  categoryTypeId,
  moments,
  planId,
  workspaceId,
}: CreatBulkUpdateParams<TSelectionMeta>): BulkUpdateRequestBody => ({
  data: {
    attributes: {
      moments,
    },
    relationships: {
      workspace: {
        data: {
          id: workspaceId,
          type: 'workspace',
        },
      },
      plan: {
        data: {
          id: planId,
          type: 'plan',
        },
      },
      category: categoryId
        ? {
            data: {
              id: categoryId,
              type: 'category',
            },
          }
        : undefined,
      category_type: categoryTypeId
        ? {
            data: {
              id: categoryTypeId,
              type: 'category_type',
            },
          }
        : undefined,
    },
    type: 'add_to_plan',
  },
})

export const useCreateBulkMomentsToPlanRequest = <
  TSelectionMeta extends Record<string, unknown>,
>({
  onMutate,
  onSuccess,
}: QueryParams<TSelectionMeta>) => {
  return useMutation({
    mutationFn: (params: CreatBulkUpdateParams<TSelectionMeta>) => {
      return bulkUpdateBFFOperations.momentsToPlan({
        requestBody: buildRequestBody(params),
      })
    },
    onMutate,
    onSuccess,
  })
}
